import { FC, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, RotateCcw, Upload } from "lucide-react";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import $api from "../../shared/api/axios";
import { TRevisionEntry } from "./revision";

const SKILL_MD = "SKILL.md";

type TProps = {
  revisionId: number;
  revisionLabel: number;
  entry: TRevisionEntry;
  isDraft: boolean;
  onSaved: () => void;
};

export const SkillFolderEditor: FC<TProps> = ({ revisionId, revisionLabel, entry, isDraft, onSaved }) => {
  const [selected, setSelected] = useState<string>(SKILL_MD);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const archiveBase = `/admin/prompts/revisions/${revisionId}/entries/${entry.promptKey}/archive`;

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return $api.post(archiveBase, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },
    onSuccess: () => {
      toast.success("Skill folder replaced from the ZIP");
      setSelected(SKILL_MD);
      onSaved();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to import the ZIP")
  });

  const download = useMutation({
    mutationFn: async () => {
      const response = await $api.get(archiveBase, { responseType: "blob" });
      const url = URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${entry.promptKey}-r${revisionLabel}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: () => toast.error("Failed to download the ZIP")
  });

  const reset = useMutation({
    mutationFn: () => $api.post(`/admin/prompts/revisions/${revisionId}/entries/${entry.promptKey}/reset`),
    onSuccess: () => {
      toast.success("Skill folder reset to the active revision");
      setSelected(SKILL_MD);
      onSaved();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to reset the skill folder")
  });

  const onReset = () => {
    if (window.confirm("Resetting discards every change to this skill folder in the draft. Continue?")) {
      reset.mutate();
    }
  };

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (window.confirm("Uploading a ZIP replaces the entire skill folder for this draft. Continue?")) {
      upload.mutate(file);
    }
  };

  const paths = [SKILL_MD, ...entry.files.map((file) => file.path)];
  const bundleDiffersFromActive = entry.differsFromActive || entry.files.some((file) => file.differsFromActive);
  const selectedContent =
    selected === SKILL_MD ? entry.content : entry.files.find((file) => file.path === selected)?.content ?? "";

  return (
    <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
      <div className="px-6 py-4 border-b bg-gray-50 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[14px] font-medium text-gray-900">{entry.label}</div>
          <FigmaTypography component="div" variant="body-3" className="text-gray-400 mt-0.5">
            {entry.promptKey} · skill folder · {paths.length} {paths.length === 1 ? "file" : "files"}
          </FigmaTypography>
          {entry.description && (
            <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-2">
              {entry.description}
            </FigmaTypography>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {bundleDiffersFromActive && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-[12px] leading-[18px] text-amber-800">
              Differs from active revision
            </span>
          )}
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2">
              {isDraft && bundleDiffersFromActive && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AppButton
                      appearance="outlined"
                      size="icon"
                      aria-label="Reset to active revision"
                      onClick={onReset}
                      isLoading={reset.isPending}
                      className="shrink-0"
                    >
                      <RotateCcw />
                    </AppButton>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-[12px] leading-[18px]">
                    Reset to active revision
                  </TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <AppButton
                    appearance="outlined"
                    size="icon"
                    aria-label="Download ZIP"
                    onClick={() => download.mutate()}
                    isLoading={download.isPending}
                    className="shrink-0"
                  >
                    <Download />
                  </AppButton>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-[12px] leading-[18px]">
                  Download ZIP
                </TooltipContent>
              </Tooltip>
              {isDraft && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <AppButton
                        size="icon"
                        aria-label="Upload ZIP"
                        onClick={() => fileInputRef.current?.click()}
                        isLoading={upload.isPending}
                        className="shrink-0"
                      >
                        <Upload />
                      </AppButton>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-[12px] leading-[18px]">
                      Upload ZIP (replaces the folder)
                    </TooltipContent>
                  </Tooltip>
                  <input ref={fileInputRef} type="file" accept=".zip" className="hidden" onChange={onPickFile} />
                </>
              )}
            </div>
          </TooltipProvider>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 py-4 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-56">
          <ul className="space-y-0.5">
            {paths.map((path) => {
              const differs =
                path === SKILL_MD
                  ? entry.differsFromActive
                  : entry.files.find((f) => f.path === path)?.differsFromActive;
              return (
                <li key={path}>
                  <button
                    type="button"
                    onClick={() => setSelected(path)}
                    className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-[12px] ${
                      selected === path ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <span className="truncate font-mono">{path}</span>
                    {differs && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <div className="min-w-0 flex-1">
          <pre className="max-h-[560px] overflow-auto rounded border border-gray-200 bg-gray-50 px-3 py-2 whitespace-pre-wrap font-mono text-[12px] leading-[18px] text-gray-800">
            {selectedContent}
          </pre>
        </div>
      </div>
    </div>
  );
};
